/**
 * Console sections that have a navigation entry and a route but no backend.
 *
 * Kept as data next to the nav definition so the two cannot drift: every `available: false` entry in
 * `PLATFORM_NAV` should have a matching route here, and `missingApi` records what has to be built
 * before the section can hold anything real.
 */
export interface PlatformPlaceholderRoute {
  path: string;
  title: string;
  description: string;
  missingApi: string;
}

export const PLATFORM_PLACEHOLDER_ROUTES: PlatformPlaceholderRoute[] = [
  {
    path: "users",
    title: "Platform Users",
    description: "Manage platform administrators and support staff",
    missingApi:
      "platform user/role CRUD endpoints — the PlatformRole and PlatformUserRole model exists but no controller exposes it",
  },
  {
    path: "plans",
    title: "Plans & Subscriptions",
    description: "Define plans and manage tenant subscriptions",
    missingApi:
      "plan/subscription endpoints — plan tier is a string on Tenant, and UpdateTenantSubscriptionCommand is not exposed over HTTP",
  },
  {
    path: "payments",
    title: "Payments",
    description: "SaaS billing and tenant invoicing",
    missingApi:
      "a SaaS billing module — the existing /api/payments endpoints are school fee collection, not platform billing",
  },
  {
    path: "audit-logs",
    title: "Audit Logs",
    description: "Cross-tenant platform activity trail",
    missingApi:
      "a platform-scope audit log endpoint — GET /api/user-access/audit-logs is filtered to the caller's tenant",
  },
  {
    path: "feature-flags",
    title: "Feature Flags",
    description: "Toggle features per tenant or plan",
    missingApi:
      "a feature flag endpoint — the PermissionPlan tier mapping exists but is not readable or editable over HTTP",
  },
  {
    path: "settings",
    title: "System Settings",
    description: "Platform-wide configuration",
    missingApi: "a platform settings endpoint — no global settings controller exists",
  },
  {
    path: "integrations",
    title: "Integrations",
    description: "Third-party services connected to the platform",
    missingApi:
      "a platform integrations endpoint — gateway settings today are per-tenant under /api/payments/admin",
  },
  {
    path: "notifications",
    title: "Platform Notifications",
    description: "Broadcast announcements across tenants",
    missingApi:
      "a platform broadcast endpoint — the notification APIs are all tenant-scoped",
  },
  {
    path: "health",
    title: "Platform Health",
    description: "Service availability and system metrics",
    missingApi:
      "a metrics endpoint — /health/app and /health/db return liveness only, with no uptime, latency or queue data",
  },
];
